import React, { useEffect, useMemo, useState } from 'react';
import { Input, message } from 'antd';
import { CustomButton, CustomTitle } from '@/components';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import { filtersList } from '@/common/static_data';
import { SentimentKey } from '@/pages/AiHistory/components/SentimentFilter';
import { toLabel } from '@/pages/Shipments/Filters/utils/labels';

import {
  AppliedFilter,
  FilterArrayItem,
} from '@/pages/Shipments/ControlTower/types';
import { AIStateKey } from '@/pages/AiHistory/components/AIStateFilter';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

export interface FilterPreset {
  id: string;
  name: string;
  filterArray: FilterArrayItem[];
  appliedFilters: AppliedFilter[];
  createdAt: number;
  updatedAt: number;
}

interface SaveFilterSetModalProps {
  aiStateSelected?: AIStateKey[];
  sentimentSelected?: SentimentKey[];
  open: boolean;
  onClose: () => void;

  presets: FilterPreset[];

  filterArray: FilterArrayItem[];
  appliedFilters: AppliedFilter[];

  selectedPresetId?: string | null;

  onSave: (payload: { name: string }) => void | Promise<void>;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 36,
  fontSize: 14,
  backgroundColor: 'var(--light-gray)',
  border: 'none',
  borderRadius: 6,
  paddingLeft: 12,
};

const overwriteHintWrapperStyle: React.CSSProperties = {
  minHeight: 20,
  marginTop: 8,
};

const actionsRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 20,
};

type FilterListItem = {
  key: string;
  label: string;
};
const FILTER_LABEL_BY_KEY: Record<string, string> = (
  filtersList as FilterListItem[]
).reduce<Record<string, string>>((acc, item) => {
  if (item.key) {
    acc[item.key] = item.label ?? String(item.key);
  }
  return acc;
}, {});

const DATE_KEYS = new Set<string>([
  'scheduled_pickup_date',
  'scheduled_delivery_date',
  'customer_defined_ship_date',
  'created_at',
]);

const formatMonthYear = (
  iso?: string | null,
  dateFormat?: string | null
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d);
  const year = d.getFullYear();

  // Year-first preference (YYYY/MM/DD) shows "2025 Jan" instead of "Jan 2025";
  // month/day-first preferences don't affect this month+year-only label since
  // there's no day component to reorder.
  return dateFormat?.startsWith('YYYY')
    ? `${year} ${month}`
    : `${month} ${year}`;
};

const buildDateLabel = (
  af: AppliedFilter,
  dateFormat?: string | null
): string | null => {
  const anyAf = af;
  const start = anyAf.start as string | undefined;
  const end = anyAf.end as string | undefined;

  const startLabel = formatMonthYear(start, dateFormat);
  const endLabel = formatMonthYear(end, dateFormat);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  if (typeof anyAf.name === 'string' && anyAf.name) {
    return anyAf.name.replace(/^.*?:\s*/, '');
  }

  return null;
};

const buildDefaultNameFromFilters = (
  appliedFilters: AppliedFilter[] | undefined | null,
  aiStateSelected?: AIStateKey[] | null,
  sentimentSelected?: SentimentKey[] | null,
  dateFormat?: string | null
): string => {
  const parts: string[] = [];
  const seen = new Set<string>();
  if (aiStateSelected?.length) {
    let aiPart: string;

    if (aiStateSelected.length === 1) {
      aiPart = `AI State: ${aiStateSelected[0]}`;
    } else {
      aiPart = `AI State +${aiStateSelected.length}`;
    }

    if (!seen.has(aiPart)) {
      seen.add(aiPart);
      parts.push(aiPart);
    }
  }
  if (sentimentSelected?.length) {
    let sPart: string;

    if (sentimentSelected.length === 1) {
      sPart = `Sentiment: ${sentimentSelected[0]}`;
    } else {
      sPart = `Sentiment +${sentimentSelected.length}`;
    }

    if (!seen.has(sPart)) {
      seen.add(sPart);
      parts.push(sPart);
    }
  }

  if (!appliedFilters || appliedFilters.length === 0) {
    return parts.join(', ');
  }

  const groups = new Map<string, AppliedFilter[]>();

  for (const af of appliedFilters) {
    const key =
      (af.filterBy as string | undefined) ||
      (af.dateFilter as string | undefined) ||
      (typeof af.id === 'string' ? af.id : undefined);

    if (!key) continue;
    const arr = groups.get(key) ?? [];
    arr.push(af);
    groups.set(key, arr);
  }

  const orderedKeys: string[] = [];
  for (const item of filtersList as FilterListItem[]) {
    if (item?.key && groups.has(item.key)) {
      orderedKeys.push(item.key);
    }
  }
  for (const key of Array.from(groups.keys())) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  }

  for (const key of orderedKeys) {
    const group = groups.get(key);
    if (!group || group.length === 0) continue;

    if (DATE_KEYS.has(key)) {
      for (const af of group) {
        const label = buildDateLabel(af, dateFormat);
        if (!label) continue;
        const trimmed = label.trim();
        if (!trimmed || seen.has(trimmed)) continue;
        seen.add(trimmed);
        parts.push(trimmed);
      }
      continue;
    }

    const count = group.length;

    if (count === 1) {
      const only = group[0];
      const valueLabel =
        only.name ?? only.title ?? only.label ?? String(only.id);
      const trimmed = String(valueLabel).trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      parts.push(trimmed);
      continue;
    }

    const label = FILTER_LABEL_BY_KEY[key] ?? toLabel(key);
    const extra = count - 1;
    const part = `${label} +${extra}`;
    const trimmed = part.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    parts.push(trimmed);
  }

  return parts.join(', ');
};

const SaveFilterSetModal: React.FC<SaveFilterSetModalProps> = ({
  aiStateSelected,
  sentimentSelected,
  open,
  onClose,
  // presets,
  appliedFilters,
  filterArray,
  selectedPresetId = null,
  onSave,
}) => {
  const { dateFormat } = useUserDateTimeFormat();
  const [name, setName] = useState('');

  const hasAnyFilters = useMemo(
    () =>
      (appliedFilters && appliedFilters.length > 0) ||
      (filterArray && filterArray.length > 0),
    [appliedFilters, filterArray]
  );

  useEffect(() => {
    if (!open) return;
    const defaultName = buildDefaultNameFromFilters(
      appliedFilters,
      aiStateSelected,
      sentimentSelected,
      dateFormat
    );
    setName(defaultName);
  }, [open, appliedFilters, aiStateSelected, sentimentSelected, dateFormat]);

  const isOverwrite = !!selectedPresetId;
  const saveCta = isOverwrite ? 'Overwrite' : 'Save filter set';
  const canSave = name.trim().length > 0 && hasAnyFilters;

  const handleSave = async () => {
    if (!hasAnyFilters) {
      message.info('No filters to save');
      return;
    }
    if (!name.trim()) {
      message.warning('Please enter a name');
      return;
    }

    try {
      await onSave({ name: name.trim() });
    } catch {
      // parent shows error
    }
  };

  return (
    <CustomModalWithoutButtons
      modalOpen={open}
      setModalOpen={onClose}
      fontSize='20px'
      modalTextFontSize='14px'
      modalTitle='Name filter set'
      modalSize={600}
    >
      <div style={containerStyle}>
        <Input
          style={inputStyle}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Enter a name'
        />

        {isOverwrite && (
          <div style={overwriteHintWrapperStyle}>
            <CustomTitle
              text='A preset will be overwritten with this save.'
              fontSize='12px'
              color='#6E6E6E'
              fontWeight='400'
            />
          </div>
        )}

        <div style={actionsRowStyle}>
          <CustomButton
            key='cancel'
            text='Cancel'
            onClick={onClose}
            bgColor='transparent'
            color='var(--text-primary)'
            border='none'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='15px 15px 15px 0px'
            borderRadius='8px'
          />

          <CustomButton
            key='save'
            text={saveCta}
            onClick={handleSave}
            bgColor='#000000'
            color='#FFFFFF'
            fontSize='14px'
            width='120px'
            borderRadius='8px'
            fontWeight='600'
            height='36px'
            isFormValid={!canSave}
          />
        </div>
      </div>
    </CustomModalWithoutButtons>
  );
};

export default SaveFilterSetModal;
